import * as amplitude from '@amplitude/analytics-browser';
import { CookieConsent, OneTrustConsentTypes, whenOneTrustReady } from '@udemy/onetrust';
import { captureException } from '@udemy/sentry';
import { getVisitorUUID, getConfigData, udMe } from '@udemy/shared-utils';
import { udApiStat } from '@udemy/ud-api';
import { useUserHasInstructorOptionsQuery } from './graphql/api-platform-graphql';
import { resolveCartContents } from './resolvers/cart-resolver';
import { FunnelEvent, RevenueEvent } from './types/funnel-event';
const AMPLITUDE_UPSAMPLED_PROJECT_KEYS = {
  ET_LOGGED_IN_USERS_UPSAMPLED: 'f353e4295f82c640a261c73311de1ef3',
  ET_VISITORS_UPSAMPLED: '5a3d2e29ec033cbf81792799577f2433'
};
const AMPLITUDE_UNSAMPLED_PROJECT_KEYS = {
  CLIENT_SIDE_SEND_UNSAMPLED_TEST: 'aec7e6e3aefc91db90634b4a1a361239',
  CLIENT_SIDE_SEND_UNSAMPLED_PROD: '7269f546a4a4ed714cb73fe42704dd11'
};

/**
 * Enum for the types of Amplitude projects available to use.
 * - UPSAMPLED: The upsampled project is used.
 * - UNSAMPLED: The unsampled project is used.
 */
var AmplitudeProjectType = /*#__PURE__*/function (AmplitudeProjectType) {
  AmplitudeProjectType[AmplitudeProjectType["UPSAMPLED"] = 0] = "UPSAMPLED";
  AmplitudeProjectType[AmplitudeProjectType["UNSAMPLED"] = 1] = "UNSAMPLED";
  return AmplitudeProjectType;
}(AmplitudeProjectType || {});
/**
 * Enum for configuring how Amplitude projects are used within the application.
 * - ONLY_UPSAMPLED: Only upsampled project(s) are activated.
 * - ONLY_UNSAMPLED: Only unsampled project(s) are activated.
 * - DUAL_TRACK: Both upsampled and unsampled projects are activated.
 */
export let AmplitudeProjectSelection = /*#__PURE__*/function (AmplitudeProjectSelection) {
  AmplitudeProjectSelection["ONLY_UPSAMPLED"] = "ONLY_UPSAMPLED";
  AmplitudeProjectSelection["ONLY_UNSAMPLED"] = "ONLY_UNSAMPLED";
  AmplitudeProjectSelection["DUAL_TRACK"] = "DUAL_TRACK";
  return AmplitudeProjectSelection;
}({});

/**
 * A named project descriptor: the API key to init with, the project type, and
 * the BrowserClient instance created for it. Instances are created at load time
 * (before consent) so that pre-consent track() calls are buffered by the SDK
 * until init() is called.
 */

/**
 * True once buildProjects() has run: all instances are created and ready to
 * accept track() calls (which the SDK will buffer until init() is called).
 */
let isAmplitudeLoaded = false;

/**
 * True once init() has been called on all instances (i.e. OneTrust advertising
 * consent has been granted and performInit() has run).
 */
let isAmplitudeInitialized = false;

// True when the SDK is staged (window.amplitude set) and waiting for advertising consent
let isAmplitudeAwaitingConsent = false;
let currentApiKey = null;
let currentLocale = null;
let currentUserId = null;
let currentProjectSelection = null;

/**
 * Promise that coordinates when the Amplitude SDK is ready to use via `whenAmplitudeReady()`.
 */
let amplitudeInitReadyResolve;
const amplitudeInitReady = new Promise(resolve => {
  amplitudeInitReadyResolve = resolve;
});

/**
 * The bare `amplitude` module reference staged on `window.amplitude`.
 */
let amplitudeInstance = null;

/**
 * The primary project — always the first entry created by buildProjects().
 * Explicit reference avoids ambiguous array indexing; this instance receives
 * identify() calls, the engagement plugin, and is the `default` in
 * getAmplitudeInstances().
 */
let primaryProject = null;

/**
 * All active projects in insertion order. The primary project is always first.
 * All entries receive every track() call.
 */
let activeProjects = [];

/**
 * Clean up existing Amplitude instances before re-initialization.
 * Flushes pending events and resets all module-level state.
 */
const cleanupAmplitudeInstances = async () => {
  for (const project of activeProjects) {
    try {
      // eslint-disable-next-line @typescript-eslint/await-thenable
      await project.instance.flush();
    } catch (error) {
      captureException(new Error(`Amplitude failed to flush instance for key ${project.apiKey}`));
    }
  }
  primaryProject = null;
  activeProjects = [];
  amplitudeInstance = null;
  isAmplitudeLoaded = false;
  isAmplitudeInitialized = false;
  isAmplitudeAwaitingConsent = false;
  currentApiKey = null;
  currentUserId = null;
  currentProjectSelection = null;
  currentLocale = null;
  if (typeof window !== 'undefined') {
    window.amplitude = undefined;
  }
};

/**
 * Options for initializing Amplitude Guides & Surveys
 */

/**
 * Updates the locale for Amplitude Guides & Surveys (internal function)
 * This will re-fetch the configuration with the new locale and display guides/surveys in the new language
 * @param locale - The new language code (e.g., 'en', 'es', 'fr', 'en-US')
 * @internal - Use initializeAmplitudeGS with locale option instead
 */
const updateAmplitudeLocale = async locale => {
  /* istanbul ignore next - Defensive check, already validated by initializeAmplitude */
  if (typeof window === 'undefined') {
    return;
  }

  /* istanbul ignore next - Defensive check, already validated by initializeAmplitude */
  if (!isAmplitudeInitialized || !primaryProject) {
    return;
  }

  /* istanbul ignore next - Defensive check for same locale */
  if (locale === currentLocale) {
    return;
  }

  // Per the Amplitude Guides & Surveys docs, locale is set at plugin-init time.
  // The only way to change locale at runtime is to remove the existing plugin
  // and re-add it with the new locale.
  try {
    await primaryProject.instance.remove('engagement');

    // Lazy dynamic import — same SSR guard rationale as performInit.
    const {
      plugin: engagementPlugin
    } = await import('@amplitude/engagement-browser');
    const pluginConfig = {
      serverZone: 'US',
      locale
    };
    const guidesPlugin = engagementPlugin(pluginConfig);
    await primaryProject.instance.add(guidesPlugin);
    currentLocale = locale;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('[Amplitude] Failed to update locale:', error);
  }
};

/**
 * Builds the ordered list of project descriptors for the given selection.
 * Each project gets a pre-created BrowserClient instance so that track()
 * calls buffer before init() is called on consent.
 *
 * The first entry is always the primary project.
 */
const buildProjects = (projectSelection, primaryApiKey, udConfigEnv) => {
  const initOptions = {
    serverZone: 'US'
  };
  // Disable all autocapture features including pageViews.
  // Page views must NOT be autocaptured at init time because the SPA router
  // may not have resolved the current route yet, resulting in wrong page URL/location.
  // Consumers should call trackAmplitudePageView() after the router has resolved.
  const initOptionsUnsampled = {
    serverZone: 'US',
    autocapture: {
      attribution: false,
      pageViews: true,
      sessions: false,
      formInteractions: false,
      fileDownloads: false,
      elementInteractions: false,
      pageUrlEnrichment: false,
      webVitals: false
    }
  };
  if (projectSelection === AmplitudeProjectSelection.ONLY_UPSAMPLED) {
    return [{
      projectType: AmplitudeProjectType.UPSAMPLED,
      apiKey: primaryApiKey,
      initOptions,
      instance: amplitude.createInstance()
    }];
  }
  if (projectSelection === AmplitudeProjectSelection.ONLY_UNSAMPLED) {
    return [{
      projectType: AmplitudeProjectType.UNSAMPLED,
      apiKey: primaryApiKey,
      initOptions: initOptionsUnsampled,
      instance: amplitude.createInstance()
    }];
  }

  // DUAL_TRACK: primary = upsampled, secondary = unsampled
  const unsampledApiKey = udConfigEnv === 'PROD' ? AMPLITUDE_UNSAMPLED_PROJECT_KEYS.CLIENT_SIDE_SEND_UNSAMPLED_PROD : AMPLITUDE_UNSAMPLED_PROJECT_KEYS.CLIENT_SIDE_SEND_UNSAMPLED_TEST;
  return [{
    projectType: AmplitudeProjectType.UPSAMPLED,
    apiKey: primaryApiKey,
    initOptions,
    instance: amplitude.createInstance()
  }, {
    projectType: AmplitudeProjectType.UNSAMPLED,
    apiKey: unsampledApiKey,
    initOptions: initOptionsUnsampled,
    instance: amplitude.createInstance()
  }];
};

/**
 * Initialize Amplitude and mark the SDK as ready for downstream usage.
 *
 * **Lifecycle**
 * 1. **Loaded**: `buildProjects()` runs immediately — all SDK instances are
 *    created and `isAmplitudeLoaded` becomes true. track() calls made from
 *    this point are buffered by the SDK per-instance.
 * 2. **Awaiting consent**: `whenOneTrustReady` registers the OneTrust callback.
 *    `isAmplitudeAwaitingConsent` prevents duplicate registrations.
 * 3. **Initialized**: Once advertising consent (C0004) is granted, `init()` is
 *    called on every project instance. The SDK flushes its buffer automatically.
 *    `isAmplitudeInitialized` becomes true.
 *
 * @param options - Optional configuration for Amplitude initialization
 * @returns The Amplitude SDK instance on success, undefined otherwise
 */
export const initializeAmplitude = async options => {
  var _udMe, _udConfig$brand, _udConfig$brand2;
  if (typeof window === 'undefined') {
    return;
  }
  const me = (_udMe = udMe()) !== null && _udMe !== void 0 ? _udMe : {};
  const udConfig = getConfigData();
  const isLoggedIn = !!me.id;
  const projectSelection = (options === null || options === void 0 ? void 0 : options.projectSelection) || AmplitudeProjectSelection.DUAL_TRACK;
  const locale = options === null || options === void 0 ? void 0 : options.locale;
  /* istanbul ignore next */
  const organization = (_udConfig$brand = udConfig.brand) !== null && _udConfig$brand !== void 0 && _udConfig$brand.has_organization ? udConfig.brand.organization : null;
  const organizationId = organization === null || organization === void 0 ? void 0 : organization.id;
  const organizationSendUserDataTo3rdPartyEnabled = !!(organization !== null && organization !== void 0 && organization.is_send_user_data_to_3rd_party_enabled);
  const organization3rdPartySourcesEnabled = !!((_udConfig$brand2 = udConfig.brand) !== null && _udConfig$brand2 !== void 0 && _udConfig$brand2.is_external_sources_enabled);

  // For org users, skip SDK import entirely if either 3rd-party flag is disabled.
  // Non-org (marketplace) users are unaffected by this guard.
  if (organizationId && (!organization3rdPartySourcesEnabled || !organizationSendUserDataTo3rdPartyEnabled)) {
    return;
  }

  // Resolve the primary (upsampled/engagement) API key
  let primaryApiKey = '';
  if (projectSelection === AmplitudeProjectSelection.ONLY_UNSAMPLED) {
    primaryApiKey = udConfig.env === 'PROD' ? AMPLITUDE_UNSAMPLED_PROJECT_KEYS.CLIENT_SIDE_SEND_UNSAMPLED_PROD : AMPLITUDE_UNSAMPLED_PROJECT_KEYS.CLIENT_SIDE_SEND_UNSAMPLED_TEST;
  } else {
    // ONLY_UPSAMPLED or DUAL_TRACK primary
    primaryApiKey = isLoggedIn ? AMPLITUDE_UPSAMPLED_PROJECT_KEYS.ET_LOGGED_IN_USERS_UPSAMPLED : AMPLITUDE_UPSAMPLED_PROJECT_KEYS.ET_VISITORS_UPSAMPLED;
  }
  const userId = me.id || getVisitorUUID();
  if (!userId) {
    return;
  }

  // Already initialized with the same configuration — resolve for late subscribers
  // and handle any locale change, then return.
  if (isAmplitudeInitialized && currentApiKey === primaryApiKey && currentUserId === userId && currentProjectSelection === projectSelection) {
    if (amplitudeInstance) {
      amplitudeInitReadyResolve(amplitudeInstance);
    }
    if (locale && locale !== currentLocale) {
      await updateAmplitudeLocale(locale);
    }
    return;
  }

  // Already staged and waiting for advertising consent — avoid duplicate handlers
  if (isAmplitudeAwaitingConsent) {
    return;
  }

  // Clean up stale instances before re-initialization
  if (isAmplitudeLoaded) {
    await cleanupAmplitudeInstances();
  }
  try {
    var _activeProjects$;
    window.amplitude = amplitude;
    amplitudeInstance = amplitude;

    // Loaded: create all project instances now (before consent) so that track()
    // calls buffer per-instance and are flushed when init() is called on consent.
    activeProjects = buildProjects(projectSelection, primaryApiKey, udConfig.env);
    primaryProject = /* istanbul ignore next */(_activeProjects$ = activeProjects[0]) !== null && _activeProjects$ !== void 0 ? _activeProjects$ : null;
    isAmplitudeLoaded = true;

    /**
     * Called once consent is granted. Initializes every project instance in
     * order, sets user properties on the primary, and attaches the engagement
     * plugin to the primary.
     */
    const performInit = async () => {
      var _activeProjects$find;
      if (isAmplitudeInitialized) {
        return;
      }
      /* istanbul ignore next - primaryProject is always set before performInit is called */
      if (!primaryProject) {
        return;
      }
      for (const project of activeProjects) {
        project.instance.init(project.apiKey, userId, project.initOptions);
      }
      if (typeof window !== 'undefined' && window.location) {
        const identify = new amplitude.Identify();
        identify.set('ub_domain', window.location.hostname);
        primaryProject.instance.identify(identify);
      }

      // Set organization group on the unsampled instance (if present).
      const unsampledProject = (_activeProjects$find = activeProjects.find(p => p.projectType === AmplitudeProjectType.UNSAMPLED)) !== null && _activeProjects$find !== void 0 ? _activeProjects$find : null;
      if (unsampledProject) {
        if (organizationId) {
          unsampledProject.instance.setGroup('organization', String(organizationId));
          const groupIdentifyEvent = new amplitude.Identify();
          groupIdentifyEvent.set('organization_id', organizationId);
          unsampledProject.instance.groupIdentify('organization', String(organizationId), groupIdentifyEvent);
        }
      }

      // Lazy dynamic import so the module is never evaluated during SSR/ISR
      // (its CJS bundle accesses window at the top level).
      // This is safe here because performInit only runs after the
      // `typeof window === 'undefined'` guard in initializeAmplitude.
      const {
        plugin: engagementPlugin
      } = await import('@amplitude/engagement-browser');
      const pluginConfig = {
        serverZone: 'US',
        ...(locale && {
          locale
        })
      };
      primaryProject.instance.add(engagementPlugin(pluginConfig));
      isAmplitudeInitialized = true;
      isAmplitudeAwaitingConsent = false;
      currentApiKey = primaryApiKey;
      currentUserId = userId;
      currentLocale = locale || null;
      currentProjectSelection = projectSelection;
      amplitudeInitReadyResolve(amplitude);
      udApiStat.increment('amplitude.web_sdk.initialized', {
        outcome: 'success',
        project_selection: projectSelection
      });
    };
    isAmplitudeAwaitingConsent = true;
    // whenOneTrustReady is void in production (pushes to a queue); in tests
    // the mock returns a Promise so that `await initializeAmplitude()` waits
    // for the async performInit to complete before assertions run.
    await whenOneTrustReady(async () => {
      // For UB Amplitude is categorized as stricly necessary, for MX we classify Amplitude as performance.
      const amplitudePermissionsGranted = !organizationId ? CookieConsent.allowsPerformanceCookies() : CookieConsent.allows(OneTrustConsentTypes.STRICTLY_NECESSARY);
      if (amplitudePermissionsGranted) {
        await performInit();
      } else {
        var _window$OneTrust;
        /* istanbul ignore next - window.OneTrust is guaranteed by whenOneTrustReady */
        (_window$OneTrust = window.OneTrust) === null || _window$OneTrust === void 0 || _window$OneTrust.OnConsentChanged(async consentEvent => {
          if (consentEvent.detail.includes(OneTrustConsentTypes.ADVERTISING)) {
            await performInit();
          }
        });
      }
    });
    return amplitude;
  } catch (error) {
    udApiStat.increment('amplitude.web_sdk.initialized', {
      outcome: 'fail',
      project_selection: projectSelection
    });
    captureException(error);
  }
};

/**
 * Wait until Amplitude is ready, then invoke a callback.
 * This mirrors `whenBrazeReady` for a consistent integration surface.
 * @param cb - Callback invoked with the Amplitude SDK instance.
 */
export async function whenAmplitudeReady(cb) {
  const amplitude = await amplitudeInitReady;
  cb(amplitude);
  return amplitude;
}

/**
 * Result of getAmplitudeInstances() providing access to active Amplitude instances.
 */

/**
 * Get access to the active Amplitude instances.
 * Use this when you need direct access to the instances for advanced use cases.
 *
 * @returns Object containing the available Amplitude instances based on project selection mode
 */
export function getAmplitudeInstances() {
  var _upsampledProject$ins, _unsampledProject$ins;
  const upsampledProject = activeProjects.find(p => p.projectType === AmplitudeProjectType.UPSAMPLED);
  const unsampledProject = activeProjects.find(p => p.projectType === AmplitudeProjectType.UNSAMPLED);
  return {
    default: amplitudeInstance,
    /* istanbul ignore next */
    upsampled: (_upsampledProject$ins = upsampledProject === null || upsampledProject === void 0 ? void 0 : upsampledProject.instance) !== null && _upsampledProject$ins !== void 0 ? _upsampledProject$ins : null,
    /* istanbul ignore next */
    unsampled: (_unsampledProject$ins = unsampledProject === null || unsampledProject === void 0 ? void 0 : unsampledProject.instance) !== null && _unsampledProject$ins !== void 0 ? _unsampledProject$ins : null,
    projectSelection: currentProjectSelection
  };
}

/**
 * Track an event to all active Amplitude projects.
 *
 * The Amplitude SDK buffers track() calls internally before init() is called,
 * so this works correctly both before and after OneTrust consent is granted.
 * Pre-consent events are queued per-instance and flushed automatically when
 * init() is called at consent time.
 *
 * @param event - An AmplitudeEvent instance to track
 */
export function trackAmplitudeEvent(event) {
  if (!isAmplitudeLoaded) {
    return;
  }
  if (event instanceof FunnelEvent) {
    resolveCartContents(event);
  }
  const eventName = event.eventName;
  const properties = event.toEventProperties();
  try {
    // Send events only to unsampled project(s)
    const unsampledProjects = activeProjects.filter(p => p.projectType === AmplitudeProjectType.UNSAMPLED);
    for (const project of unsampledProjects) {
      // Track revenue via Amplitude's Revenue API for RevenueEvent instances
      if (event instanceof RevenueEvent) {
        for (const item of event.getRevenueObjects()) {
          const revenueObj = new amplitude.Revenue().setPrice(item.price).setQuantity(item.quantity).setRevenueType(item.revenueType).setCurrency(item.currency);
          if (item.productId != null) {
            revenueObj.setProductId(String(item.productId));
          }
          project.instance.revenue(revenueObj);
        }
      }
      // Always track the event properties
      project.instance.track(eventName, properties);
    }
    udApiStat.increment('amplitude.web_sdk.event_sent', {
      event_name: eventName,
      project_selection: /* istanbul ignore next - currentProjectSelection is always set when instances exist */
      currentProjectSelection || AmplitudeProjectSelection.ONLY_UPSAMPLED
    });
  } catch (error) {
    /* istanbul ignore next - Error handling for unexpected track failures */
    captureException(error);
    /* istanbul ignore next - Error handling for unexpected track failures */
    udApiStat.increment('amplitude.web_sdk.event_not_sent.error', {
      event_name: eventName,
      project_selection: currentProjectSelection || AmplitudeProjectSelection.ONLY_UPSAMPLED
    });
  }
}

/**
 * Cached promise for the instructor check so the GraphQL query is only issued once
 * per page load regardless of how many times checkIsInstructor is called.
 */
let cachedIsInstructor = null;

/**
 * Returns whether the current user is an instructor.
 * Returns false immediately for logged-out users and organization users.
 * The result is cached after the first network call.
 */
export const checkIsInstructor = () => {
  var _udMe2, _udConfig$brand3;
  if (cachedIsInstructor !== null) {
    return cachedIsInstructor;
  }
  const me = (_udMe2 = udMe()) !== null && _udMe2 !== void 0 ? _udMe2 : {};
  const udConfig = getConfigData();
  if (!me.id || (_udConfig$brand3 = udConfig.brand) !== null && _udConfig$brand3 !== void 0 && _udConfig$brand3.has_organization) {
    return Promise.resolve(false);
  }
  const promise = useUserHasInstructorOptionsQuery.fetcher()().then(data => {
    var _data$me;
    return !!((_data$me = data.me) !== null && _data$me !== void 0 && _data$me.hasInstructorOptions);
  }).catch(error => {
    captureException(error);
    return false;
  });
  cachedIsInstructor = promise;
  return promise;
};

/**
 * Reset all module-level state. Intended for use in tests only.
 * @internal
 */
export const _resetStateForTesting = /* istanbul ignore next */() => {
  isAmplitudeLoaded = false;
  isAmplitudeInitialized = false;
  isAmplitudeAwaitingConsent = false;
  amplitudeInstance = null;
  primaryProject = null;
  activeProjects = [];
  currentApiKey = null;
  currentUserId = null;
  currentLocale = null;
  currentProjectSelection = null;
  cachedIsInstructor = null;
};
//# sourceMappingURL=bootstrap-amplitude-gs.js.map